/**
 * httpClient.js — Centralized HTTP client for AriaOne API
 *
 * All API calls flow through this module.
 * - Auth token attached automatically from localStorage
 * - Demo mode write blocking via modeGuard
 * - Response normalization
 * - Error normalization
 * - Retry logic for transient failures
 */
import { isDemoMode } from '../modeGuard';
import { bumpEndpoint } from '../scheduler/endpointCounters';

/**
 * Base URL resolution — vendor vs local.
 *
 * When `REACT_APP_VENDOR_MODE=true`, scheduler (and any future vendor-backed)
 * calls resolve to `REACT_APP_VENDOR_API_URL` (default: https://ehi.ariaone.ai/api).
 * Otherwise they resolve to `REACT_APP_BACKEND_URL` (local/dev/preview backend).
 *
 * Individual callers can force one or the other via `opts.vendor`:
 *   - `opts.vendor === true`  → always use vendor URL (ignores VENDOR_MODE)
 *   - `opts.vendor === false` → always use local URL (ignores VENDOR_MODE)
 *   - `opts.vendor` unset      → follow VENDOR_MODE env toggle
 */
const LOCAL_API = process.env.REACT_APP_BACKEND_URL;
// Vendor API raw value accepts either form (`…/api` or `…`). We normalize by
// stripping a single trailing `/api` so callers whose paths already start
// with `/api/scheduler/…` (matching the spec and the local backend) don't
// end up with a doubled `/api/api/scheduler/…` when vendor mode flips on.
const VENDOR_API_RAW = process.env.REACT_APP_VENDOR_API_URL || 'https://ehi.ariaone.ai/api';
const VENDOR_API = VENDOR_API_RAW.replace(/\/api\/?$/i, '');
const VENDOR_MODE_ENABLED = String(process.env.REACT_APP_VENDOR_MODE || '').toLowerCase() === 'true';

/**
 * Banned auth-host list.
 *
 * The AriaOne login/SSO domain (`login.*.ariaone.ai`) accepts OAuth2 flows
 * only — it does NOT host the scheduler data API. If a misconfigured env
 * ever points a data request at a login host we refuse the request with a
 * normalized error rather than silently 403'ing. The guard runs on every
 * resolved base URL.
 */
const LOGIN_HOST_BANLIST = [/^login(\.|$)/i, /^auth(\.|$)/i];
const isLoginHost = (baseUrl) => {
  try {
    const host = new URL(baseUrl).hostname;
    return LOGIN_HOST_BANLIST.some((re) => re.test(host));
  } catch { return false; }
};

/**
 * Paths that MUST always resolve against the same origin as the app
 * (never against the vendor domain), regardless of `REACT_APP_VENDOR_MODE`
 * or `opts.vendor`.  Browser fetches to these endpoints must not trigger
 * a cross-origin preflight — that failed in production when the frontend
 * was served from `login.ariaone.ai` and the scheduler calls resolved to
 * `ehi.ariaone.ai`, blocking booking.
 *
 * Keep this list aligned with the hotfix charter:
 *   • /api/scheduler/*         (appointments, slots/available, daily-summary, locations, entities, …)
 *   • /api/patients            (patient search used by BookingModal)
 */
const SAME_ORIGIN_ONLY_PATHS = [
  /^\/api\/scheduler(\/|$)/,
  /^\/api\/patients(\/|$|\?)/,
  /^\/api\/user-access(\/|$)/,
  /^\/api\/fhir\/subscriptions(\/|$)/,
  /^\/api\/ops(\/|$)/,
];

/** True when `path` is locked to the app's own origin. */
const isSameOriginOnlyPath = (path) =>
  !!path && SAME_ORIGIN_ONLY_PATHS.some((re) => re.test(path));

const resolveBaseUrl = (vendor, path) => {
  // Same-origin lock — scheduler + patients + user-access MUST stay on the
  // app's own origin so the browser never issues a cross-origin preflight to
  // `ehi.ariaone.ai` (which would fail CORS when the app is served from
  // `login.ariaone.ai` / `crm.ariaone.ai`).  Vendor integration continues
  // to run server-to-server on the backend.  `opts.vendor === true` is
  // still honoured on other paths (eg. auth bootstrap) but NEVER on the
  // locked list above.
  if (isSameOriginOnlyPath(path)) {
    return LOCAL_API;
  }
  if (vendor === true) return VENDOR_API;
  if (vendor === false) return LOCAL_API;
  return VENDOR_MODE_ENABLED ? VENDOR_API : LOCAL_API;
};

// Fail fast at module load if either env was misconfigured to a login host.
if (LOCAL_API && isLoginHost(LOCAL_API)) {
  // eslint-disable-next-line no-console
  console.error('[httpClient] REACT_APP_BACKEND_URL points to a login host — data requests will be refused.', LOCAL_API);
}
if (isLoginHost(VENDOR_API)) {
  // eslint-disable-next-line no-console
  console.error('[httpClient] REACT_APP_VENDOR_API_URL points to a login host — data requests will be refused.', VENDOR_API);
}

// Back-compat export: default (non-vendor) base URL.
const API = LOCAL_API;
export { LOCAL_API, VENDOR_API, VENDOR_MODE_ENABLED, LOGIN_HOST_BANLIST, isLoginHost, resolveBaseUrl, SAME_ORIGIN_ONLY_PATHS, isSameOriginOnlyPath };

/**
 * Normalized error shape returned by all API calls on failure.
 * @typedef {Object} ApiError
 * @property {number} status    — HTTP status code (0 for network errors)
 * @property {string} code      — Machine-readable error code
 * @property {string} message   — Human-readable message
 * @property {Object} [details] — Optional extra context
 */

/**
 * Build auth headers from current token.
 *
 * Attaches `Authorization: Bearer <token>` (vendor spec) and an optional
 * `X-Organization-Id` so vendor + local backends receive identical org
 * context. Both web and mobile clients MUST follow this same pattern to
 * maintain JWT/claims parity and avoid 403 drift.
 */
const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('ariaone-token');
  const orgId = localStorage.getItem('ariaone-org-id');
  const tenantId = localStorage.getItem('ariaone-tenant-id');
  const userId = localStorage.getItem('ariaone-user-id');
  const headers = { 'Content-Type': 'application/json', ...extra };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (orgId) headers['X-Organization-Id'] = orgId;
  if (tenantId) headers['X-Tenant-Id'] = tenantId;
  if (userId) headers['X-User-Id'] = userId;
  return headers;
};

/**
 * Normalize an error response into a consistent shape.
 *
 * 401/403 receive distinct codes so scheduler (and other) panels can surface
 * the right user-facing message:
 *   - 401 → SESSION_EXPIRED (session/auth issue, tell user to sign in again)
 *   - 403 → ORG_PERMISSION_DENIED (permission/org mismatch — data-level denial)
 */
const normalizeError = async (res) => {
  let body = {};
  try { body = await res.json(); } catch { /* empty */ }
  let code = body.code || body.error || `HTTP_${res.status}`;
  let message = body.message || body.detail || res.statusText || 'Request failed';
  if (res.status === 401) {
    code = body.code || 'SESSION_EXPIRED';
    message = body.message || body.detail || 'Your session has expired — please sign in again.';
  } else if (res.status === 403) {
    code = body.code || 'ORG_PERMISSION_DENIED';
    message = body.message || body.detail || 'Permission denied — your organization may not have access to this resource.';
  }
  return {
    status: res.status,
    code,
    message,
    details: body.details || body.errors || null,
  };
};

/**
 * Core request function.
 *
 * @param {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} method
 * @param {string} path — Relative path (e.g. '/api/team/members')
 * @param {Object} [options]
 * @param {Object} [options.body]    — JSON body (auto-stringified)
 * @param {Object} [options.params]  — URL query params
 * @param {Object} [options.headers] — Extra headers
 * @param {number} [options.retries] — Number of retries on 5xx/network (default 1)
 * @returns {Promise<{data: *, ok: true} | {error: ApiError, ok: false}>}
 */
export const request = async (method, path, options = {}) => {
  const { body, params, headers: extraHeaders, retries = 1, vendor } = options;

  // Block writes in demo mode
  const isWrite = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  if (isWrite && isDemoMode()) {
    return {
      ok: false,
      error: { status: 0, code: 'DEMO_MODE', message: 'Write blocked in demo mode', details: { method, path } },
    };
  }

  // Build URL (vendor vs local base). `resolveBaseUrl` now takes the path
  // so we can enforce the same-origin lock for scheduler + patients
  // regardless of the vendor-mode toggle.
  const base = resolveBaseUrl(vendor, path);

  // Refuse any request resolved against a login/SSO host. The login host
  // only accepts OAuth2 flows and would 403 every data request; better to
  // fail fast with a clear code than silently parrot a 403 to the UI.
  //
  // EXCEPTION — same-origin-locked paths (scheduler, patients, user-access)
  // are routed through the app's own origin by design.  In production the
  // app IS served from `login.ariaone.ai` / `crm.ariaone.ai`, so a blanket
  // login-host block would reject every data request we've explicitly
  // same-origin locked.  The lock guarantees these paths go through our
  // backend (which proxies to the vendor server-to-server), so the
  // OAuth-only safety gate doesn't apply.
  if (isLoginHost(base) && !isSameOriginOnlyPath(path)) {
    return {
      ok: false,
      error: {
        status: 0,
        code: 'LOGIN_HOST_BLOCKED',
        message: 'Refused to route data request to a login/SSO host. Check REACT_APP_VENDOR_API_URL / REACT_APP_BACKEND_URL.',
        details: { base, path },
      },
    };
  }

  // Dev-only: warn if an authenticated scheduler request is missing the org
  // header — the #1 cause of 403 drift between web and mobile clients.
  if (process.env.NODE_ENV !== 'production' && path.startsWith('/api/scheduler/')) {
    const hasToken = !!localStorage.getItem('ariaone-token');
    const hasOrg = !!localStorage.getItem('ariaone-org-id');
    if (hasToken && !hasOrg) {
      // eslint-disable-next-line no-console
      console.warn('[httpClient] Scheduler call missing X-Organization-Id — expect vendor 403.', { method, path });
    }
  }

  let url = `${base}${path}`;
  if (params) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, String(v));
    });
    const qsStr = qs.toString();
    if (qsStr) url += `?${qsStr}`;
  }

  const fetchOpts = {
    method,
    headers: authHeaders(extraHeaders),
  };
  if (body && isWrite) {
    fetchOpts.body = JSON.stringify(body);
  }

  let lastError = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, fetchOpts);
      // Scheduler-only ops debug counter (PHI-free — path × status family).
      bumpEndpoint(path, res.status);

      if (res.ok) {
        // Handle 204 No Content
        if (res.status === 204) return { ok: true, data: null };
        const data = await res.json();
        return { ok: true, data };
      }

      // Non-retryable client errors
      if (res.status < 500) {
        const error = await normalizeError(res);
        return { ok: false, error };
      }

      // Server error — retry
      lastError = await normalizeError(res);
    } catch (err) {
      bumpEndpoint(path, 0);
      lastError = {
        status: 0,
        code: 'NETWORK_ERROR',
        message: err.message || 'Network error',
        details: null,
      };
    }

    // Wait before retry (exponential backoff: 500ms, 1000ms)
    if (attempt < retries) {
      await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
    }
  }

  return { ok: false, error: lastError };
};

/* ═══ Convenience methods ═══ */
export const get    = (path, params, opts) => request('GET', path, { ...opts, params });
export const post   = (path, body, opts)   => request('POST', path, { ...opts, body });
export const put    = (path, body, opts)   => request('PUT', path, { ...opts, body });
export const patch  = (path, body, opts)   => request('PATCH', path, { ...opts, body });
export const del    = (path, opts)         => request('DELETE', path, opts);

/**
 * Require a parameter or throw.
 */
export const requireParam = (name, value) => {
  if (value === undefined || value === null || value === '') {
    throw new Error(`Missing required parameter: ${name}`);
  }
};
